import React, { useEffect, useMemo, useState, useCallback } from "react";
// Requires: npm i @monaco-editor/react
import Editor from "@monaco-editor/react";

/**
 * NexusQueryEditor
 * - Controlled modal that hosts a Monaco Editor for authoring SQL (or other) queries
 * - Shows Cancel and Apply buttons; Apply is enabled only when content differs from initialQuery
 * - Parent controls visibility via isOpen and handles onApply/onCancel
 *
 * Props:
 * - isOpen: boolean (controls visibility)
 * - initialQuery?: string (default "")
 * - language?: string (default "sql")
 * - title?: string (default "SQL Query Editor")
 * - height?: string/number (default "60vh")
 * - theme?: "vs-dark" | "light" | string (default "vs-dark")
 * - readOnly?: boolean (default false)
 * - onApply: (query: string) => void
 * - onCancel: () => void
 */
const NexusQueryEditor = ({
  isOpen,
  initialQuery = "",
  language = "sql",
  title = "SQL Query Editor",
  height = "40vh",
  theme = "light",
  readOnly = false,
  onApply,
  onCancel,
}) => {
  const [value, setValue] = useState(initialQuery || "");
  const [mounted, setMounted] = useState(false);

  // Reset editor value each time dialog is opened
  useEffect(() => {
    if (isOpen) {
      setValue(initialQuery || "");
      setMounted(true);
    }
  }, [isOpen, initialQuery]);

  const isDirty = useMemo(
    () => (value ?? "") !== (initialQuery ?? ""),
    [value, initialQuery]
  );

  // const handleApply = useCallback(async () => {
  //   if (!isDirty) return;
  //   if (!onApply) return;

  //   // Execute query via alasql and forward results (or error) to caller
  //   try {
  //     const { results, error } = await runQuery(value);
  //     onApply(value, { results, error });
  //   } catch (e) {
  //     onApply(value, { results: null, error: e });
  //   }
  // }, [onApply, isDirty, value]);

  const handleApply = useCallback(() => {
    if (!isDirty) return;
    if (!onApply) return;

    onApply(value);
  }, [onApply, isDirty, value]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      // Ctrl/Cmd + Enter to apply
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (isDirty) handleApply();
      }
      // Escape to cancel
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel && onCancel();
      }
    },
    [isOpen, isDirty, handleApply, onCancel]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => onCancel && onCancel()}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="nexus-query-editor-title"
        className="relative w-[80vw] max-w-3xl bg-white rounded-lg shadow-xl border border-slate-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div
            id="nexus-query-editor-title"
            className="text-base font-medium text-slate-800"
          >
            {title}
          </div>
          <div className="text-xs text-slate-500">
            {isDirty ? "Unsaved changes" : "No changes"}
          </div>
        </div>

        {/* Editor */}
        <div className="px-4 pt-3">
          <div className="rounded border border-slate-200 bg-white">
            {mounted && (
              <Editor
                height={height}
                defaultLanguage={language}
                value={value}
                theme={theme}
                options={{
                  minimap: { enabled: false },
                  automaticLayout: true,
                  lineNumbers: "on",
                  wordWrap: "on",
                  readOnly,
                }}
                onChange={(val) => setValue(val ?? "")}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button
            type="button"
            className="px-3 py-2 rounded border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
            onClick={() => onCancel && onCancel()}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isDirty}
            className={`px-3 py-2 rounded text-white text-sm ${
              isDirty
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-slate-300 cursor-not-allowed"
            }`}
            onClick={handleApply}
            title={isDirty ? "Apply (Ctrl/Cmd+Enter)" : "No changes to apply"}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default NexusQueryEditor;
